import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { applicantsActions } from '_actions';
import { AddEditNote } from '_components/utils/AddEditNote/AddEditNote';
import { ApplicantNoteCard } from '_components/utils/ApplicantNoteCard/ApplicantNoteCard';

const ApplicantNotesSection = ({ applicants, isSupAdmin }) => {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const permission = useSelector((state) => state.permission);
  const notes = applicants?.data?.notes || [];
  const applicantHash = applicants?.data?.hash;

  const canEditNotes = permission?.permissions?.data?.includes(
    'Update Applicant Note',
  );
  const loggedInUserId = user?.profile?.data?.id;

  const [editableNote, setEditableNote] = useState({ note: '', id: '' });
  const [noteEditMode, setNoteEditMode] = useState(false);
  const [isNoteLoading, setIsNoteLoading] = useState(false);

  const onEditNoteClick = useCallback((note) => {
    setEditableNote({ id: note.id, note: note.notes });
    setNoteEditMode(true);
  }, []);

  const onChangeEditNote = useCallback((value) => {
    setEditableNote((prev) => ({ ...prev, note: value }));
  }, []);

  const onCancelAddEditNote = useCallback(() => {
    setEditableNote({ note: '', id: '' });
    setNoteEditMode(false);
  }, []);

  const updateNotes = useCallback(async () => {
    const { id, note } = editableNote;
    const data = new FormData();
    data.append('applicant_hash', applicantHash);
    data.append('notes', note);
    data.append('note_id', noteEditMode ? id : '');

    setIsNoteLoading(true);
    const res = await dispatch(applicantsActions.updateNote(data));

    if (res?.data?.success) {
      toast.success(res.data.message);
      setNoteEditMode(false);
      setEditableNote({ id: '', note: '' });
      await dispatch(applicantsActions.viewDetails(applicantHash));
    } else {
      toast.error('Creating note failed!');
    }

    setIsNoteLoading(false);
  }, [editableNote, noteEditMode, dispatch, applicantHash]);

  const deleteApplicantNote = useCallback(
    async (note) => {
      const data = new FormData();
      data.append('note_id', note.id);

      setIsNoteLoading(true);
      const res = await dispatch(applicantsActions.deleteNote(data));

      if (res?.data?.success) {
        toast.success(res.data.message);
        await dispatch(applicantsActions.viewDetails(applicantHash));
      }

      setIsNoteLoading(false);
    },
    [dispatch, applicantHash],
  );

  return (
    <div className='applicant-notes-wrapper'>
      <h3>Notes</h3>
      <div className='note-editor'>
        {(canEditNotes || isSupAdmin) && !noteEditMode && (
          <AddEditNote
            note={editableNote.note}
            value={editableNote.note}
            onChange={onChangeEditNote}
            onCancel={onCancelAddEditNote}
            disabled={!editableNote.note}
            textBoxDisabled={false}
            onSaveNote={updateNotes}
            loading={isNoteLoading}
          />
        )}
        <div className='applicant-notes'>
          {!!notes.length &&
            notes.map((note) =>
              note.id === editableNote.id ? (
                <AddEditNote
                  key={note.id}
                  disabled={!editableNote.note}
                  note={note}
                  value={editableNote.note}
                  onChange={onChangeEditNote}
                  onCancel={onCancelAddEditNote}
                  onSaveNote={updateNotes}
                  loading={isNoteLoading}
                />
              ) : (
                <ApplicantNoteCard
                  key={note.id}
                  note={note}
                  loading={isNoteLoading}
                  onEditNoteClick={onEditNoteClick}
                  onDeleteNoteClick={deleteApplicantNote}
                  canEditOrDelete={
                    note?.author?.id === loggedInUserId || isSupAdmin
                  }
                />
              ),
            )}
        </div>
      </div>
    </div>
  );
};

ApplicantNotesSection.propTypes = {
  applicants: PropTypes.object.isRequired,
  isSupAdmin: PropTypes.bool.isRequired,
};

export default ApplicantNotesSection;
